import type { FieldErrors } from '../api/errors';
import { selectHeading, selectParagraph, selectRequireZip, shouldShowError } from '../state/selectors';
import type { AddressField, UserField } from '../state/types';
import { systemFieldRequirement } from '../domain/validation';
import { useEnv, useFormState } from './context';
import { Field } from './fields/Field';
import { SelectField, TextField } from './fields/inputs';
import { PinIcon } from './icons';
import { Section } from './Section';
import { SlotPickerSection } from './SlotPickerSection';

interface Props {
  errors: FieldErrors;
}

export function Step1({ errors }: Props) {
  const { controller, config } = useEnv();
  const state = useFormState();
  const form = state.bootstrap?.form ?? null;
  const settings = state.bootstrap?.settings ?? null;
  const requireZip = selectRequireZip(state);
  const req = (code: string) => systemFieldRequirement(form, code, { requireZip });
  const show = (key: string) => (shouldShowError(state, key, 1) ? [...(errors[key] ?? []), ...(state.apiErrors[key] ?? [])] : []);
  const dedupe = (list: string[]) => Array.from(new Set(list));
  const user = (field: UserField) => ({
    value: state.values.user[field],
    onChange: (value: string) => controller.send({ type: 'USER_FIELD', field, value }),
    onBlur: () => controller.send({ type: 'FIELD_BLURRED', key: `user.${field}` }),
  });
  const addr = (field: AddressField) => ({
    value: state.values[field],
    onChange: (value: string) => controller.send({ type: 'ADDRESS_FIELD', field, value }),
    onBlur: () => controller.send({ type: 'FIELD_BLURRED', key: field }),
  });
  const states = settings?.country?.states ?? {};
  // The tenant lists every state in its country; the plugin's service-states setting narrows
  // that to where the business actually works. One state → the field is locked to it.
  const served = config.serviceStates.filter((code) => states[code]);
  const stateOptions = Object.entries(states)
    .filter(([value]) => served.length === 0 || served.includes(value))
    .map(([value, label]) => ({ value, label: String(label) }));
  const lockedState = served.length === 1 ? served[0]! : null;
  const showCity = settings?.address?.show_city !== false && req('city').shown;
  const showState = settings?.address?.show_state !== false && req('state').shown;
  const showZip = settings?.address?.show_zip !== false && req('zip').shown;

  return (
    <>
      <Section heading={selectHeading(state, 'you_are_section')} paragraph={selectParagraph(state, 'contact_information')}>
        <div className="cbf-row cbf-row-2">
          {req('first_name').shown ? <TextField fieldKey="user.first_name" label={req('first_name').label} required={req('first_name').required} errors={dedupe(show('user.first_name'))} autoComplete="given-name" {...user('first_name')} /> : null}
          {req('last_name').shown ? <TextField fieldKey="user.last_name" label={req('last_name').label} required={req('last_name').required} errors={dedupe(show('user.last_name'))} autoComplete="family-name" {...user('last_name')} /> : null}
        </div>
        <div className="cbf-row cbf-row-2">
          {req('email').shown ? <TextField fieldKey="user.email" type="email" label={req('email').label} required={req('email').required} errors={dedupe(show('user.email'))} autoComplete="email" inputMode="email" {...user('email')} /> : null}
          {req('phone').shown ? <TextField fieldKey="phone" type="tel" label={req('phone').label} required={req('phone').required} errors={dedupe(show('phone'))} autoComplete="tel" inputMode="tel" {...addr('phone')} /> : null}
        </div>
      </Section>

      <Section heading={selectHeading(state, 'your_home_section')} paragraph={selectParagraph(state, 'address_information')}>
        <div className="cbf-row cbf-row-address">
          <TextField fieldKey="address" label={req('address').label} required={req('address').required} errors={dedupe(show('address'))} autoComplete="address-line1" {...addr('address')} />
          {req('apt_suite').shown ? <TextField fieldKey="apt_suite" label={req('apt_suite').label} required={req('apt_suite').required} errors={dedupe(show('apt_suite'))} autoComplete="address-line2" {...addr('apt_suite')} /> : null}
        </div>
        <div className="cbf-row cbf-row-3">
          {showCity ? <TextField fieldKey="city" label={req('city').label} required={req('city').required} errors={dedupe(show('city'))} autoComplete="address-level2" {...addr('city')} /> : null}
          {showState ? (
            lockedState ? (
              <Field fieldKey="state" label={req('state').label} errors={dedupe(show('state'))}>
                {({ id }) => (
                  <p className="cbf-fixed" id={id}>
                    {String(states[lockedState])}
                  </p>
                )}
              </Field>
            ) : stateOptions.length ? (
              <SelectField fieldKey="state" label={req('state').label} required={req('state').required} errors={dedupe(show('state'))} options={stateOptions} placeholder="Select…" {...addr('state')} />
            ) : (
              <TextField fieldKey="state" label={req('state').label} required={req('state').required} errors={dedupe(show('state'))} autoComplete="address-level1" {...addr('state')} />
            )
          ) : null}
          {showZip ? <TextField fieldKey="zip" label={req('zip').label} required={req('zip').required} errors={dedupe(show('zip'))} autoComplete="postal-code" inputMode="numeric" {...addr('zip')} /> : null}
        </div>
        {state.location.status === 'blocked' ? (
          <div className="cbf-callout" role="alert">
            <PinIcon />
            <div>
              <strong>We can&rsquo;t book this address online yet.</strong>
              <br />
              {state.location.message}
            </div>
          </div>
        ) : null}
        {state.location.status === 'checking' ? (
          <p className="cbf-help" role="status">
            Checking your service area…
          </p>
        ) : null}
      </Section>

      <SlotPickerSection errors={errors} />
    </>
  );
}
